import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import locale from "../../constants/locale";

type Props = {
  className?: string;
  src: string;
  name?: string;
  disabledPrimaryText?: boolean
  resolveSrc?: () => Promise<string>;
};

const download = (url: string, name: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.className = "is-hidden";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const DownloadButton = ({ className, src, name, disabledPrimaryText, resolveSrc }: Props) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  let n = src;

  try {
    const url = new URL(src).pathname.split("/");
    n = name || decodeURI(url[url.length - 1]);
  } catch (error) {
    n = name || t(locale.download);
  }
  const onDownload = async () => {
    if (!src || busy) return;
    if (!resolveSrc) { download(src, n); return; }
    // Open during the click so the async grant request does not trigger popup blocking.
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    setBusy(true);
    setError("");
    try {
      const url = await resolveSrc();
      if (!["https:", "http:", "blob:"].includes(new URL(url).protocol)) throw new Error("Invalid download URL");
      if (tab) tab.location.replace(url); else download(url, n);
    } catch (error) {
      tab?.close();
      setError(error instanceof Error ? error.message : t(locale.resourceNotFound));
    } finally { setBusy(false); }
  };
  return (
    <div
      role="button"
      tabIndex={src && !busy ? 0 : -1}
      aria-disabled={!src || busy}
      aria-busy={busy}
      className={clsx(
        "is-flex is-clickable is-align-items-center",
        className,
        {
          "has-text-primary": !disabledPrimaryText
        }
      )}
      onClick={onDownload}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") { event.preventDefault(); void onDownload(); }
      }}
    >
      <span className="icon mr-2">
        <i className="fas fa-download"></i>
      </span>
      <span>{n}</span>
      {error && <span role="alert">{error}</span>}
    </div>
  );
};
